import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Scale, CheckCircle2, HelpCircle, ShieldAlert, FileText, PenTool } from "lucide-react";
import { MatrizProbatoria } from "@/services/scraperApi";

interface MatrizProbatoriaPanelProps {
  matriz?: MatrizProbatoria;
  onGenerateDraft?: (task: any) => void;
}

export const MatrizProbatoriaPanel: React.FC<MatrizProbatoriaPanelProps> = ({ matriz, onGenerateDraft }) => {
  const [activeTab, setActiveTab] = useState<"acreditados" | "disputa" | "carga" | "vacios">("disputa");

  if (!matriz) return null;

  const acreditados = matriz.hechos_acreditados || [];
  const disputa = matriz.hechos_en_disputa || [];
  const carga = matriz.carga_de_la_prueba || [];
  const vacios = matriz.vacios_probatorios_criticos || [];

  const totalItems = acreditados.length + disputa.length + carga.length + vacios.length;
  if (totalItems === 0) return null;

  return (
    <Card className="p-6 border border-indigo-100/80 bg-white shadow-sm rounded-2xl space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-700 border border-indigo-100">
            <Scale className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider">
                Matriz Probatoria Forense
              </h3>
              <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] font-bold px-2 py-0.5">
                Hechos vs Pruebas vs Cargas
              </Badge>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Evaluación probatoria de la contienda litigiosa (Art. 167 CGP / Art. 7 y 381 CPP)
            </p>
          </div>
        </div>

        {/* Tab Buttons */}
        <div className="flex flex-wrap items-center gap-1.5 p-1 bg-gray-50 rounded-xl border border-gray-200/60">
          <button
            onClick={() => setActiveTab("disputa")}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
              activeTab === "disputa" 
                ? "bg-white text-indigo-700 shadow-xs border border-indigo-100" 
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            <HelpCircle className="h-3.5 w-3.5 text-indigo-500" />
            <span>En Disputa</span>
            <Badge className="bg-indigo-100 text-indigo-800 text-[9px] px-1.5 py-0 border-none">{disputa.length}</Badge>
          </button>

          <button
            onClick={() => setActiveTab("acreditados")}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
              activeTab === "acreditados" 
                ? "bg-white text-emerald-700 shadow-xs border border-emerald-100" 
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            <span>Acreditados</span>
            <Badge className="bg-emerald-100 text-emerald-800 text-[9px] px-1.5 py-0 border-none">{acreditados.length}</Badge>
          </button>

          <button
            onClick={() => setActiveTab("carga")}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
              activeTab === "carga" 
                ? "bg-white text-amber-700 shadow-xs border border-amber-100" 
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            <Scale className="h-3.5 w-3.5 text-amber-500" />
            <span>Carga Probatoria</span>
            <Badge className="bg-amber-100 text-amber-800 text-[9px] px-1.5 py-0 border-none">{carga.length}</Badge>
          </button>

          <button
            onClick={() => setActiveTab("vacios")}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
              activeTab === "vacios" 
                ? "bg-white text-rose-700 shadow-xs border border-rose-100" 
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            <ShieldAlert className="h-3.5 w-3.5 text-rose-500" />
            <span>Vacíos Críticos</span>
            <Badge className="bg-rose-100 text-rose-800 text-[9px] px-1.5 py-0 border-none">{vacios.length}</Badge>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div>
        {/* Pestaña: Hechos en Disputa */}
        {activeTab === "disputa" && (
          <div className="space-y-3">
            {disputa.length > 0 ? (
              disputa.map((item, idx) => (
                <div key={idx} className="p-4 bg-gradient-to-r from-indigo-50/40 via-white to-gray-50/40 rounded-xl border border-indigo-100/70 space-y-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-black text-indigo-950 uppercase tracking-wide flex items-center gap-1.5">
                      <HelpCircle className="h-4 w-4 text-indigo-600" />
                      {item.punto_controvertido}
                    </span>
                    <div className="flex items-center gap-2">
                      {item.relevancia && (
                        <Badge className="bg-indigo-100 text-indigo-700 text-[9px] font-bold border-none uppercase">
                          {item.relevancia}
                        </Badge>
                      )}
                      {onGenerateDraft && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onGenerateDraft({
                            titulo: `Objeción y Controversia: ${item.punto_controvertido}`,
                            descripcion: `Sustentar la tesis del cliente frente a: ${item.punto_controvertido}. Tesis cliente: ${item.tesis_cliente || "N/A"}.`,
                            objetivo: `Desvirtuar la postura de la contraparte (${item.tesis_contraparte || "N/A"})`
                          })}
                          className="bg-white hover:bg-indigo-50 border-indigo-200 text-indigo-700 rounded-lg text-[11px] font-bold h-7 px-2.5 shadow-xs flex items-center gap-1"
                        >
                          <PenTool className="h-3 w-3" />
                          <span>✍️ Redactar Oposición</span>
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                    <div className="p-2.5 bg-emerald-50/60 rounded-lg border border-emerald-100 text-xs">
                      <span className="text-[10px] font-black uppercase text-emerald-700 block mb-0.5">Tesis de tu Cliente</span>
                      <p className="text-gray-800 font-medium leading-relaxed">{item.tesis_cliente || "Sin tesis declarada"}</p>
                    </div>
                    <div className="p-2.5 bg-rose-50/60 rounded-lg border border-rose-100 text-xs">
                      <span className="text-[10px] font-black uppercase text-rose-700 block mb-0.5">Tesis de la Contraparte</span>
                      <p className="text-gray-800 font-medium leading-relaxed">{item.tesis_contraparte || "Sin postura conocida"}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-400 italic p-4 text-center">No hay puntos controvertidos registrados en este análisis.</p>
            )}
          </div>
        )}

        {/* Pestaña: Hechos Acreditados */}
        {activeTab === "acreditados" && (
          <div className="space-y-2.5">
            {acreditados.length > 0 ? (
              acreditados.map((item, idx) => (
                <div key={idx} className="p-3.5 bg-white rounded-xl border border-gray-100 flex items-start justify-between gap-3 shadow-xs">
                  <div className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-gray-900">{item.hecho}</p>
                      {item.prueba_soporte && (
                        <p className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1 font-medium">
                          <FileText className="h-3 w-3 text-gray-400" />
                          <span>Soporte: {item.prueba_soporte}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  {item.beneficia_a && (
                    <Badge className={`shrink-0 text-[10px] font-bold uppercase border-none ${
                      item.beneficia_a.toLowerCase() === "cliente" 
                        ? "bg-emerald-100 text-emerald-700" 
                        : "bg-amber-100 text-amber-700"
                    }`}>
                      Favorece a: {item.beneficia_a}
                    </Badge>
                  )}
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-400 italic p-4 text-center">No se han registrado hechos con plena prueba en las actuaciones.</p>
            )}
          </div>
        )}

        {/* Pestaña: Carga de la Prueba */}
        {activeTab === "carga" && (
          <div className="space-y-2.5">
            {carga.length > 0 ? (
              carga.map((item, idx) => (
                <div key={idx} className="p-3.5 bg-white rounded-xl border border-gray-100 flex items-start justify-between gap-3 shadow-xs">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-900">{item.tema}</span>
                      {item.fundamento && (
                        <span className="text-[10px] text-indigo-600 font-semibold bg-indigo-50 px-1.5 py-0.5 rounded">
                          {item.fundamento}
                        </span>
                      )}
                    </div>
                    {item.responsable && (
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        Sujeto responsable: <strong className="text-gray-700">{item.responsable}</strong>
                      </p>
                    )}
                  </div>
                  <Badge className={`shrink-0 text-[10px] font-bold uppercase border-none ${
                    item.estado === "cumplida" 
                      ? "bg-emerald-100 text-emerald-700" 
                      : item.estado === "incumplida" 
                      ? "bg-rose-100 text-rose-700" 
                      : "bg-amber-100 text-amber-700"
                  }`}>
                    {item.estado || "Pendiente"}
                  </Badge>
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-400 italic p-4 text-center">Sin asignación de cargas probatorias específicas.</p>
            )}
          </div>
        )}

        {/* Pestaña: Vacíos Probatorios Críticos */}
        {activeTab === "vacios" && (
          <div className="space-y-3">
            {vacios.length > 0 ? (
              vacios.map((item, idx) => (
                <div key={idx} className="p-4 bg-rose-50/40 rounded-xl border border-rose-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-2.5">
                    <ShieldAlert className="h-5 w-5 text-rose-600 mt-0.5 shrink-0" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-black text-rose-950 uppercase">{item.prueba_faltante}</span>
                        <Badge className="bg-rose-600 text-white text-[9px] font-bold border-none uppercase">
                          Impacto: {item.impacto || "Alto"}
                        </Badge>
                      </div>
                      <p className="text-xs text-rose-800 mt-1 font-medium">
                        👉 Acción táctica: {item.accion_sugerida || "Aportar prueba técnica o decretar contrainterrogatorio."}
                      </p>
                    </div>
                  </div>

                  {onGenerateDraft && (
                    <Button
                      size="sm"
                      onClick={() => onGenerateDraft({
                        titulo: `Solicitud de Prueba Técnica: ${item.prueba_faltante}`,
                        descripcion: item.accion_sugerida || `Solicitar formalmente al despacho la práctica de la prueba: ${item.prueba_faltante}`,
                        objetivo: `Subsanar vacío probatorio de impacto ${item.impacto || "alto"}`
                      })}
                      className="shrink-0 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold h-8 px-3.5 shadow-sm flex items-center gap-1.5"
                    >
                      <PenTool className="h-3.5 w-3.5" />
                      <span>✍️ Redactar Solicitud</span>
                    </Button>
                  )}
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-400 italic p-4 text-center">No se detectaron vacíos probatorios críticos pendientes.</p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
